import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { SOCKET_IO_URL } from '../lib/utils';

const CHAT_NAMESPACE = '/chat';

/**
 * Maps backend chat:message payload to frontend message_created shape.
 * Backend: { message: { id, text, sender_role, sender_name, created_at, media, ... }, thread_id }
 */
function mapChatMessage(payload, threadId) {
  if (!payload || typeof payload !== 'object') return null;
  const msg = payload.message;
  const tid = payload.thread_id ?? threadId;
  if (!msg) return null;
  return {
    type: 'message_created',
    thread_id: tid,
    message: {
      id: msg.id,
      text: msg.text,
      sender_role: msg.sender_role,
      sender_name: msg.sender_name,
      sender_id: msg.sender_id,
      message_type: msg.message_type,
      created_at: msg.created_at || new Date().toISOString(),
      status: msg.status,
      delivered_at: msg.delivered_at,
      read_at: msg.read_at,
      media: msg.media,
      media_ids: msg.media_ids,
      client_id: msg.client_id,
      deleted_for_all: msg.deleted_for_all,
      deleted_by_name: msg.deleted_by_name,
      actions: Array.isArray(msg.actions) ? msg.actions : [],
      ...(msg.interactive != null ? { interactive: msg.interactive } : {}),
    },
  };
}

export default function useChatSocket({
  threadId,
  token,
  enabled = true,
  /** When true, receive chat:message for any thread (staff inbox). */
  listenAllThreads = false,
  onEvent,
}) {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const onEventRef = useRef(onEvent);
  const threadIdRef = useRef(threadId);
  const listenAllRef = useRef(listenAllThreads);

  useEffect(() => {
    listenAllRef.current = listenAllThreads;
  }, [listenAllThreads]);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    threadIdRef.current = threadId;
    // When threadId changes and socket is connected, join the new thread room
    const socket = socketRef.current;
    if (socket && socket.connected && threadId) {
      socket.emit('chat:join', { thread_id: String(threadId) });
    }
  }, [threadId]);

  const sendJson = useCallback((obj) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    if (obj?.type === 'typing') {
      socket.emit('chat:typing', {});
    }
  }, []);

  useEffect(() => {
    if (!enabled || !token) {
      setConnected(false);
      return undefined;
    }

    let mounted = true;
    const socket = io(`${SOCKET_IO_URL}${CHAT_NAMESPACE}`, {
      auth: { token },
      query: { token },
      path: '/api/socket.io',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      if (!mounted) return;
      setConnected(true);
      // Only join a specific thread room if threadId is provided
      if (threadId) {
        socket.emit('chat:join', { thread_id: String(threadId) });
      }
    });

    socket.on('connect_error', (err) => {
      if (!mounted) return;
      setConnected(false);
      console.warn('Chat socket connect_error:', err?.message || err);
    });

    socket.on('disconnect', () => {
      if (!mounted) return;
      setConnected(false);
    });

    socket.on('chat:message', (payload) => {
      const current = threadIdRef.current;
      const roomId = payload?.thread_id != null ? String(payload.thread_id) : null;
      if (
        !listenAllRef.current &&
        current != null &&
        roomId != null &&
        roomId !== String(current)
      ) {
        return;
      }
      const evt = mapChatMessage(payload, roomId ?? current);
      if (evt) onEventRef.current?.(evt);
    });

    socket.on('chat:message_deleted', (payload) => {
      const { thread_id, message_id, for_everyone } = payload || {};
      if (for_everyone) {
        onEventRef.current?.({
          type: 'message_deleted_for_all',
          message_id,
          message_ids: [message_id],
          chat_room_id: thread_id,
          mode: 'hard',
        });
      } else {
        onEventRef.current?.({
          type: 'message_deleted_for_me',
          message_id,
          chat_room_id: thread_id,
        });
      }
    });

    socket.on('chat:thread_blocked', (payload) => {
      onEventRef.current?.({
        type: 'thread_blocked',
        blocked: payload?.blocked,
        blocked_by_name: payload?.blocked_by_name,
        chat_room_id: payload?.thread_id,
      });
    });

    socket.on('chat:thread_deleted', (payload) => {
      onEventRef.current?.({
        type: 'thread_deleted',
        thread_id: payload?.thread_id,
      });
    });

    socket.on('chat:typing', (payload) => {
      onEventRef.current?.({
        type: 'typing',
        sender_role: payload?.sender_role || 'customer',
        ...payload,
      });
    });

    socket.on('chat:message_delivered', (payload) => {
      const { thread_id, message_ids, delivered_at } = payload || {};
      onEventRef.current?.({
        type: 'message_delivered',
        thread_id,
        message_ids: Array.isArray(message_ids) ? message_ids : [],
        delivered_at: delivered_at || new Date().toISOString(),
      });
    });

    socket.on('chat:message_read', (payload) => {
      const { thread_id, read_at, last_read_message_id } = payload || {};
      onEventRef.current?.({
        type: 'message_read',
        thread_id,
        read_at: read_at || new Date().toISOString(),
        last_read_message_id,
      });
    });

    socket.on('chat:unread_updated', () => {
      try {
        window.dispatchEvent(new Event('chat:activity'));
      } catch (e) {
        // ignore
      }
    });

    return () => {
      mounted = false;
      socket.removeAllListeners();
      if (socket.connected) {
        socket.disconnect();
      } else {
        socket.once('connect', () => socket.disconnect());
      }
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled, token]);

  return {
    connected,
    sendJson,
  };
}
